import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { Link, Prompt } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardBody, CardHeader } from 'reactstrap';
import { Can } from '../../utilities/Can';
import { useScanner } from '../../utilities/ScannerContext';
import { get, post, put } from '../../helpers/api_helper';

import IntakePutawayModal from './IntakePutawayModal';

const API = '/api/sourcing/intake';
const errorMessage = (error) => error.response?.data?.error || error.message || 'Unable to save bins.';
const scanId = () => Array.from(window.crypto.getRandomValues(new Uint8Array(16)))
  .map((value) => value.toString(16).padStart(2, '0')).join('');
const nextBinName = (bins) => {
  let number = 1;
  while (bins.includes(String(number))) number += 1;
  return String(number);
};
const lookupPart = (url, body) => get(url, { params: { scan: body.scan } });

export function IntakeWorkspace() {
  const { t } = useTranslation();
  const { registerListener, removeListener } = useScanner();
  const inputRef = useRef(null);
  const putawayRef = useRef(null);
  const [putawayOpen, setPutawayOpen] = useState(false);
  const binInputRef = useRef(null);
  const focusTargetRef = useRef(null);
  const pendingRef = useRef(null);
  const stateRef = useRef(null);
  const queueRef = useRef([]);
  const runningRef = useRef(false);
  const mountedRef = useRef(true);
  const [bins, setBins] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [scan, setScan] = useState('');
  const [binName, setBinName] = useState('');
  const [result, setResult] = useState(null);
  const [placedIn, setPlacedIn] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      const snapshot = await get(API);
      if (!mountedRef.current) return;
      if (stateRef.current && stateRef.current.generation !== snapshot.generation) {
        pendingRef.current = null;
        setResult(null);
        setPlacedIn('');
      }
      stateRef.current = snapshot;
      setBins(snapshot.bins);
      setLoaded(true);
    } catch (loadError) {
      if (mountedRef.current) setError(errorMessage(loadError));
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    document.title = t('Sort');
    load();
    return () => { mountedRef.current = false; };
  }, [load, t]);

  useEffect(() => {
    if (loaded) inputRef.current.focus();
  }, [loaded]);

  useEffect(() => {
    if (busy || error || !focusTargetRef.current) return;
    const target = focusTargetRef.current === 'bin' ? binInputRef.current : inputRef.current;
    target.focus();
    if (focusTargetRef.current === 'bin') target.select();
    focusTargetRef.current = null;
  }, [busy, error, result]);

  useEffect(() => {
    const warnOnUnload = (event) => {
      if (queueRef.current.length || pendingRef.current) {
        event.preventDefault();
        // eslint-disable-next-line no-param-reassign
        event.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', warnOnUnload);
    return () => window.removeEventListener('beforeunload', warnOnUnload);
  }, []);

  const drainQueue = useCallback(async () => {
    if (runningRef.current || !stateRef.current) return;
    runningRef.current = true;
    setBusy(true);
    setError('');
    while (queueRef.current.length && mountedRef.current) {
      const job = queueRef.current[0];
      if (pendingRef.current && job.path === '/lookup') break;
      try {
        // Keep the original generation and scan ID when retrying a failed request.
        // eslint-disable-next-line no-await-in-loop
        const snapshot = await job.send(`${API}${job.path}`, job.body);
        if (job.path === '/lookup' && snapshot.generation !== job.body.generation) {
          throw new Error('Bins changed after a deletion or reset. Reload before scanning again.');
        }
        queueRef.current.shift();
        stateRef.current = snapshot;
        if (mountedRef.current) {
          setBins(snapshot.bins);
          if (job.path === '/lookup') {
            if (snapshot.match.bin === null) {
              const pending = {
                ...job.body,
                part: snapshot.match.part,
                pending: true,
                warnQR: !job.body.scan.includes(' '),
              };
              pendingRef.current = pending;
              setResult(pending);
              setPlacedIn('');
              setBinName(nextBinName(snapshot.bins));
              focusTargetRef.current = 'bin';
              break;
            }
            queueRef.current.unshift({ path: '/scans', body: job.body, send: post });
            // eslint-disable-next-line no-continue
            continue;
          }
          if (snapshot.result) {
            pendingRef.current = null;
            setResult(snapshot.result);
            setPlacedIn(snapshot.result.bin);
            setBinName('');
            focusTargetRef.current = 'part';
          }
          if (job.path === '/bins') {
            setBinName('');
            setPlacedIn(job.body.name);
          }
          if (job.path === '/clear') {
            pendingRef.current = null;
            setResult(null);
            setPlacedIn('');
            setScan('');
          }
        }
      } catch (saveError) {
        if (mountedRef.current) setError(errorMessage(saveError));
        break;
      }
    }
    runningRef.current = false;
    if (mountedRef.current) setBusy(false);
  }, []);

  const enqueue = useCallback((path, body, send = post, first = false) => {
    if (!stateRef.current) return;
    const job = {
      path, body: { ...body, generation: stateRef.current.generation }, send,
    };
    if (first) queueRef.current.unshift(job);
    else queueRef.current.push(job);
    drainQueue();
  }, [drainQueue]);

  const addBin = useCallback((value) => {
    const name = value.trim();
    if (!name) return;
    if (pendingRef.current) {
      const pending = pendingRef.current;
      enqueue('/scans', { scan: pending.scan, scan_id: pending.scan_id, bin: name }, post, true);
    } else {
      enqueue('/bins', { name });
    }
  }, [enqueue]);

  const processScan = useCallback((value) => {
    if (putawayOpen) {
      putawayRef.current?.scan(value);
      return;
    }
    if (!stateRef.current || error || queueRef.current.some((job) => job.path === '/clear')) return;
    if (pendingRef.current || document.activeElement === binInputRef.current) {
      if (runningRef.current) return;
      addBin(value);
      return;
    }
    if (!value.trim()) return;
    enqueue('/lookup', { scan: value, scan_id: scanId() }, lookupPart);
    setScan('');
    inputRef.current.focus();
  }, [addBin, enqueue, error, putawayOpen]);

  useEffect(() => {
    registerListener(processScan);
    return () => removeListener(processScan);
  }, [processScan, registerListener, removeListener]);

  const reset = () => {
    // eslint-disable-next-line no-alert
    if (window.confirm(t('Clear all shared bins and counts for everyone?'))) {
      queueRef.current = [];
      enqueue('/clear', {});
    }
  };

  const savePlacement = (event) => {
    event.preventDefault();
    if (pendingRef.current) {
      addBin(placedIn);
      return;
    }
    enqueue('/placement', { scan_id: result.scan_id, bin: placedIn }, put);
    inputRef.current.focus();
  };

  const disabled = !loaded || busy || !!error;
  const cancelPending = () => {
    pendingRef.current = null;
    setResult(null);
    setBinName('');
    setPlacedIn('');
    focusTargetRef.current = 'part';
    drainQueue();
  };

  return (
    <Card>
      <Prompt
        when={busy || !!result?.pending || (!!error && queueRef.current.length > 0)}
        message={t('There are unsaved scans or bin changes. Leave this page?')}
      />
      <CardHeader className="d-flex align-items-center justify-content-between gap-2">
        <h1 className="h4 mb-0">{t('Sort')}</h1>
        <div className="d-flex gap-2">
          <button type="button" className="btn btn-primary" onClick={() => setPutawayOpen(true)} disabled={disabled || !!result?.pending || !bins.length}>
            {t('Putaway...')}
          </button>
          <button type="button" className="btn btn-outline-primary" onClick={load} disabled={disabled}>
            {t('Refresh bins')}
          </button>
          <Link className="btn btn-outline-primary" to="/sourcing/intake/summary">{t('Summary')}</Link>
          <button type="button" className="btn btn-outline-danger" onClick={reset} disabled={disabled}>
            {t('Clear bins')}
          </button>
        </div>
      </CardHeader>
      {putawayOpen && (
        <IntakePutawayModal
          ref={putawayRef}
          bins={bins}
          generation={stateRef.current.generation}
          onClose={() => { setPutawayOpen(false); inputRef.current?.focus(); }}
          onDeleted={(snapshot) => {
            stateRef.current = snapshot;
            setBins(snapshot.bins);
            setResult(null);
            setPlacedIn('');
          }}
        />
      )}
      <CardBody>
        <p className="text-muted">
          {t('Scan a part to find its bin. Only the longest space-delimited token is used; ties use the first token. Each scan adds one item.')}
        </p>
        {!loaded && !error && <p>{t('Loading shared bins…')}</p>}
        {error && (
          <div role="alert" className="alert alert-danger">
            <p>{error}</p>
            <p>{t('Scanning is paused. Retry to finish pending changes. If bins were deleted or cleared elsewhere, reload this page.')}</p>
            <button type="button" className="btn btn-outline-danger" onClick={loaded ? drainQueue : load}>
              {t('Retry')}
            </button>
          </div>
        )}
        <form onSubmit={(event) => { event.preventDefault(); processScan(scan); }}>
          <label className="form-label" htmlFor="intake-scan">{t('Scan part')}</label>
          <div className="d-flex gap-2 mb-3">
            <input
              id="intake-scan"
              ref={inputRef}
              className="form-control form-control-lg"
              value={scan}
              onChange={(event) => setScan(event.target.value)}
              autoComplete="off"
              spellCheck={false}
              disabled={!loaded || !!error || !!result?.pending}
              placeholder={t('Scan here and press Enter')}
            />
            <button type="submit" className="btn btn-primary" disabled={!loaded || !!error || !!result?.pending}>{t('Find bin')}</button>
          </div>
        </form>
        {busy && <p className="text-muted">{t('Saving scans and bin changes…')}</p>}
        {result?.warnQR && <div role="alert" className="alert alert-warning">{t('QR scanned?')}</div>}
        <div role="status" aria-live="polite" aria-atomic="true">
          {result && (
            <div className="alert alert-success text-center py-4">
              <div className="display-4 fw-bold text-break">
                {result.pending ? t('New Bin') : t('Put in bin {{bin}}', { bin: result.bin })}
              </div>
              <div className="fs-4 text-break mt-2">{result.part}</div>
            </div>
          )}
        </div>
        {result && (
          <form onSubmit={savePlacement} className="mb-3">
            <label className="form-label" htmlFor="intake-placed-in">{t('Placed In')}</label>
            <div className="d-flex gap-2">
              <select
                id="intake-placed-in"
                className="form-select"
                value={placedIn}
                onChange={(event) => setPlacedIn(event.target.value)}
                disabled={disabled}
                aria-describedby="intake-placement-help"
              >
                {result.pending && <option value="">{t('Choose an existing bin')}</option>}
                {bins.map((bin) => <option key={bin} value={bin}>{bin}</option>)}
              </select>
              <button type="submit" className="btn btn-primary" disabled={disabled || !placedIn}>
                {t('Save placement')}
              </button>
            </div>
            <div id="intake-placement-help" className="form-text">
              {result.pending
                ? t('Choose an existing bin to record this item there instead.')
                : t('Moves this one scanned item to the selected bin. Future scans of this code will use that bin.')}
            </div>
          </form>
        )}
        <form className="mb-3" onSubmit={(event) => { event.preventDefault(); addBin(binName); }}>
          <label className="form-label" htmlFor="intake-bin-name">
            {result?.pending ? t('New bin name') : t('Scan or add bin')}
          </label>
          <div className="d-flex gap-2">
            <input
              id="intake-bin-name"
              ref={binInputRef}
              className="form-control"
              value={binName}
              maxLength={128}
              onChange={(event) => setBinName(event.target.value)}
              placeholder={t('e.g. A-01')}
              autoComplete="off"
              disabled={disabled}
            />
            <button type="submit" className="btn btn-outline-primary" disabled={disabled || !binName.trim()}>
              {result?.pending ? t('Use bin') : t('Add bin')}
            </button>
            {result?.pending && (
              <button type="button" className="btn btn-outline-secondary" onClick={cancelPending} disabled={disabled}>
                {t('Cancel scan')}
              </button>
            )}
          </div>
          <div className="form-text">
            {result?.pending
              ? t('Press Enter to use this bin, or scan or type a different bin name.')
              : t('Scan a bin label here to add or select it, then save the placement.')}
          </div>
        </form>
        <p className="text-muted mb-0">
          {t('{{count}} active bins. Bins and counts are saved and shared with everyone.', { count: bins.length })}
        </p>
      </CardBody>
    </Card>
  );
}

export default function Intake() {
  const { t } = useTranslation();
  return (
    <div className="page-content">
      <div className="container-fluid">
        <Can i="see" this="Sourcing"><IntakeWorkspace /></Can>
        <Can not i="see" this="Sourcing"><p>{t('Unauthorized')}</p></Can>
      </div>
    </div>
  );
}
